// Where the Orders section's controls lead (KOVA-797, spec §5). This file is the only place a neighbour ticket
// swaps a stub for the real destination (KOVA-801 intake) or adds one (KOVA-798's chats, which ship together with
// their controls), and where KOVA-802 adds the click event of a control that leads somewhere: every CTA, Track and
// the order cards' opens come through here, and views never build one of these destinations themselves (no
// `router.push('/payment/update')`, no `window.open` in a view).
// Not here, and staying in the view: the navigation inside the section — closing an order (`router.back()` or a
// push to `/orders`) and paging (`router.replace` with `?page=`) — and the view events (the section's screen view,
// the renewal card's and the details' views, an error shown: most of mobile's Orders events), which KOVA-802 adds
// where the thing is drawn.
//
// Availability is the value: a seam that is (or returns) `null` has no destination yet, and the view leaves its
// control out — no disabled button, no "coming soon", never a claim that something opened. `Banner.url` is never
// followed: billing stamps it for the mobile app (`from=app`), and the intake link's `session_id` is a bearer
// credential for intake PHI — never log it, report it or persist it. See docs/architecture/orders.md → Seams.
//
// The one file in `src/orders/` that acts — through the router or the `navigate` it is handed, and `window.open`
// for Track: no Vue, no Pinia, no I/O.
import type { LocationQuery, RouteLocationRaw, Router } from 'vue-router'
import type { OrdersBanner } from '@/api/medOrders'
import { selectRenewalAction } from '@/orders/renewalAction'

/** Opens a destination. Fire-and-forget: the control has nothing to await or report. */
export type OpenDestination = () => void

/**
 * Where an order card leads: that order's details (`/orders/:id`), with the list's query (`?page=`, the dev params)
 * kept, so the list under the drawer stays put and the way back lands on the same page. The card is a real link to
 * it (an `href`), so a modifier click opens a new tab.
 */
export function orderDetailsLocation(orderId: string, query?: LocationQuery): RouteLocationRaw {
	return { path: `/orders/${encodeURIComponent(orderId)}`, query }
}

/**
 * A click on an order card: `navigate` is the card's `RouterLink` one, which takes a plain click and leaves a
 * modifier click to the browser. Events: KOVA-802 (the card's open).
 */
export function openOrder(navigate: (event?: MouseEvent) => unknown, event: MouseEvent): void {
	void navigate(event)
}

/**
 * Track on an order on its way: the carrier page — an `https` link the model vetted (`openableUrl`, T3) — as is,
 * in a new tab, without opener or referrer (T4). Call it synchronously in the click, so no popup blocker stops it.
 * The link is customer data: never log it, report it or put it in an attribute. Events: KOVA-802.
 */
export function openTracking(url: string): void {
	window.open(url, '_blank', 'noopener,noreferrer')
}

/**
 * "Update payment" on the renewal card. Local, always available: the app's own paywall, whatever `Banner.url`
 * says (spec §5.1, Q2b) — that link is stamped `from=app`, so `PaymentSuccess` would hand off to
 * `kova://payment/done` in a browser. No history-state `entry`: `UpdatePaymentEntryPoint` is a closed list and a
 * new value is KOVA-802's call, so until then the paywall reports `'direct'`. Events: KOVA-802.
 */
export function openPaymentUpdate(router: Pick<Router, 'push'>): ReturnType<Router['push']> {
	return router.push({ path: '/payment/update' })
}

/**
 * "Complete now" on the intake card. KOVA-801: no portal-safe entry yet, so `null` for every banner and the card
 * shows its text only (spec §5.2, Q1 (a); the mobile app does the same without an openable URL). `Banner.url` as
 * served is app-stamped: in a browser the funnel ends the intake on `kova://health-check/done` and strands the
 * user on a spinner, and without `from=app` it doesn't adopt the session. Replace when KOVA-801 delivers a
 * portal entry and return. Events: KOVA-802.
 */
export function intakeDestination(_banner: OrdersBanner): OpenDestination | null {
	return null
}

/**
 * The renewal card's button, or `null` when the card has none: no renewal action in the feed, or an intake
 * without a destination. Picks the same banner as `renewalBannerModel`, so the button always matches the card.
 */
export function renewalCardDestination(
	banners: readonly OrdersBanner[] | null,
	router: Pick<Router, 'push'>,
): OpenDestination | null {
	const item = selectRenewalAction(banners ?? [])
	if (item === null) return null
	if (item.action === 'payment_needed') {
		return () => {
			void openPaymentUpdate(router)
		}
	}
	return intakeDestination(item)
}
